import { lazy, Suspense } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/PageShell";
import { SITE, SITE_URL } from "@/lib/site";
import { useContactInfo } from "@/components/site/ContactInfoProvider";
import { WhatsAppLink } from "@/components/site/WhatsAppLink";
import { seoMeta, canonicalLink, absoluteUrl, robotsMeta } from "@/lib/seo";

const LeadForm = lazy(() =>
  import("@/components/site/LeadForm").then((m) => ({ default: m.LeadForm })),
);

/** Placeholder mientras carga el chunk lazy de LeadForm, para no dejar un
 * espacio en blanco en conexiones lentas. */
function LeadFormSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="mx-auto w-full max-w-xl animate-pulse rounded-lg bg-background p-6 shadow-pop md:max-w-none md:p-8"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="h-12 rounded-md bg-border/60" />
        <div className="h-12 rounded-md bg-border/60" />
        <div className="h-12 rounded-md bg-border/60" />
        <div className="h-12 rounded-md bg-border/60" />
      </div>
      <div className="mt-4 h-24 rounded-md bg-border/60" />
      <div className="mt-4 h-12 rounded-md bg-border/60" />
    </div>
  );
}

const DEFAULT_TITLE = "Cotiza tu Activación o Evento BTL en Lima | Improve Yourself";
const DEFAULT_DESCRIPTION =
  "Solicita propuesta para tu activación BTL, evento corporativo, trade marketing o sampling en Lima. Presupuesto desglosado y respuesta en menos de 72 horas.";

export const Route = createFileRoute("/cotizar/")({
  component: Cotizar,
  head: ({ match }) => {
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/cotizar",
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: canonicalLink(override?.canonicalPath ?? "/cotizar"),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Cotizar", item: `${SITE_URL}/cotizar` },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Cotizar · Improve Yourself",
            url: absoluteUrl("/cotizar"),
            inLanguage: "es-PE",
            about: {
              "@type": "LocalBusiness",
              "@id": `${SITE_URL}/#organization`,
            },
          }),
        },
      ],
    };
  },
});

function Cotizar() {
  const contact = useContactInfo();
  return (
    <PageShell>
      <PageHero
        eyebrow="· Cotizar"
        h1="Cuéntanos tu activación o evento. Armamos la propuesta."
        intro="Comparte el objetivo, la fecha y la zona. Te contactamos en menos de 24 horas hábiles para afinar el brief y te enviamos la propuesta en menos de 72 horas. Sin compromiso."
      />

      <section className="pb-24">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 md:px-8 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <h2 className="font-display text-2xl font-bold text-secondary md:text-3xl">
              O escríbenos directamente
            </h2>
            <ul className="mt-5 space-y-3 text-secondary/80">
              <li>
                <WhatsAppLink className="font-semibold text-primary hover:underline">
                  WhatsApp · {contact.phone}
                </WhatsAppLink>
              </li>
              <li>
                <a
                  href={`mailto:${contact.email}`}
                  className="font-semibold text-primary hover:underline"
                >
                  {contact.email}
                </a>
              </li>
              <li>
                {SITE.city}, {SITE.country}
              </li>
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-secondary/70">
              ¿Aún estás definiendo qué necesitas? Consulta la guía de{" "}
              <Link
                to="/blog/$slug"
                params={{ slug: "todo-lo-que-hace-una-agencia-btl-en-lima-por-tu-marca" }}
                className="font-semibold text-primary hover:underline"
              >
                servicios, proceso y costos de una agencia BTL
              </Link>{" "}
              para preparar tu requerimiento antes de cotizar.
            </p>
            <div className="mt-8 rounded-2xl border border-border bg-surface p-5 text-secondary/80">
              <p className="font-display text-lg font-bold text-secondary">¿Qué pasa después?</p>
              <ol className="mt-3 list-decimal space-y-2 pl-5">
                <li>Recibes confirmación de que llegó tu solicitud.</li>
                <li>Te contactamos para aclarar dudas del brief.</li>
                <li>Te enviamos propuesta y presupuesto detallado.</li>
              </ol>
            </div>
          </div>
          <Suspense fallback={<LeadFormSkeleton />}>
            <LeadForm />
          </Suspense>
        </div>
      </section>
    </PageShell>
  );
}
